import { PAGE_SIZE } from '@tshmanager/chat-shared'
import { h } from 'preact'
import { useEffect, useRef } from 'preact/hooks'
import { useSignal } from '@preact/signals'
import { createPhotoSwipeLightbox } from '../../../lib/photoswipe-lightbox.js'
import { MessageBubble } from './MessageBubble.jsx'
import { chatApi } from '../chat-api.js'
import { t } from '../chat-i18n.js'
import { applyWatermarksToReceipts } from '../chat-cable.js'
import {
  readChatScrollPersist,
  registerChatScrollList,
  scrollSideEffectsSuppressed,
  snapshotChatScrollList,
  unregisterChatScrollList,
} from '../../../lib/chat_scroll_persist.js'

const NEAR_BOTTOM_THRESHOLD = 120

// How close (px) to the top of the scrollable list the user must be before an
// older page is fetched. Deliberately much larger than the mobile app's
// TOP_PRELOAD_THRESHOLD (700px, see chat/src/components/chat/ConversationList.tsx)
// so the fetch has plenty of scroll-time head start and older messages are
// already prepended well before the user visually hits the top — this also
// makes the trigger far more forgiving inside the narrow/short floating panel,
// where a too-tight margin made it easy to overshoot past the trigger point
// and never fire at all.
const TOP_PRELOAD_THRESHOLD = 1600

let notificationAudio = null

function playNotificationSound() {
  try {
    if (!notificationAudio) {
      notificationAudio = new Audio('/sounds/notification.mp3')
      notificationAudio.volume = 0.5
    }
    notificationAudio.currentTime = 0
    notificationAudio.play().catch(() => { /* autoplay blocked — ignore */ })
  } catch { /* Audio unsupported */ }
}

/**
 * MessageList — scrollable message container with:
 *   - Scroll-position-based infinite scroll (load older messages), mirroring
 *     the mobile app's onScroll/TOP_PRELOAD_THRESHOLD approach rather than an
 *     IntersectionObserver sentinel
 *   - Smart scroll-to-bottom (within threshold or own message)
 *   - Auto-pin on initial load and room change (even when messages arrive async)
 *   - Delivered ACK on first render of other's message
 *   - Notification sound for live incoming messages from others
 *
 * Props: { store, cable, scrollPersistKey? }
 */
export function MessageList({ store, cable, scrollPersistKey = null }) {
  const listRef           = useRef(null)
  const contentRef        = useRef(null)
  const seenIdsRef        = useRef(new Set())
  const soundReadyRef     = useRef(false)
  const loadingRef        = useRef(false)
  const readyForScrollRef = useRef(false)
  const prevLengthRef     = useRef(0)
  const initialPinDoneRef = useRef(false)
  // True while the user is (or was last known to be) at the bottom of the list.
  // Set to true by programmatic scrolls; updated continuously by the scroll handler.
  const pinnedToBottomRef  = useRef(false)
  // Always points at the current render's loadOlder() — the scroll listener
  // itself is attached once on mount, so it must never call the closure
  // captured at that time (which would stay stuck with the initial
  // hasMore/oldestId/roomId, from before the first page even loaded).
  const loadOlderRef      = useRef(null)
  // Timestamp of the last new-message or room-open scroll — gates image-load re-pins.
  const lastMsgScrollAtRef = useRef(0)

  // Hides the message content until the list is scrolled to the bottom and
  // ready to reveal. This prevents the "flash of messages" effect when
  // opening a room: content renders invisibly, then fades in once pinned.
  const isRevealed = useSignal(false)

  const messages   = store.currentMessages.value
  const roomId     = store.activeRoomId.value
  const hasMore    = store.hasMoreByRoom.value[roomId] ?? false
  const oldestId   = store.oldestIdByRoom.value[roomId] ?? null
  const personId   = store.personId
  const isLoadingMsgs = store.loadingRoomMsgs.value === roomId

  // Reset state when room changes. pinToBottomOnOpen() is intentionally NOT called
  // here — the messages.length useEffect below handles the initial pin once the
  // first message batch arrives (guarded by initialPinDoneRef). Calling it here
  // as well caused a double-scroll: once on the empty list (attempt >= 20 fallback)
  // and again when messages loaded.
  useEffect(() => {
    seenIdsRef.current        = new Set()
    soundReadyRef.current     = false
    readyForScrollRef.current = false
    loadingRef.current        = false
    prevLengthRef.current     = 0
    initialPinDoneRef.current = false
    pinnedToBottomRef.current = false
    isRevealed.value          = false
  }, [roomId]) // eslint-disable-line

  useEffect(() => {
    if (!scrollPersistKey) return undefined
    registerChatScrollList(scrollPersistKey, () => listRef.current)
    return () => unregisterChatScrollList(scrollPersistKey)
  }, [scrollPersistKey])

  useEffect(() => {
    if (!scrollPersistKey) return undefined
    const syncPinnedAfterTurbo = () => {
      requestAnimationFrame(() => {
        const list = listRef.current
        const saved = readChatScrollPersist(scrollPersistKey)
        if (!list || !saved) return
        pinnedToBottomRef.current = saved.pinned || isNearBottom(list)
        isRevealed.value = true
      })
    }
    document.addEventListener('turbo:render', syncPinnedAfterTurbo)
    document.addEventListener('turbo:load', syncPinnedAfterTurbo)
    return () => {
      document.removeEventListener('turbo:render', syncPinnedAfterTurbo)
      document.removeEventListener('turbo:load', syncPinnedAfterTurbo)
    }
  }, [scrollPersistKey]) // eslint-disable-line

  // PhotoSwipe — init once on mount; event delegation handles dynamically added
  // items. Uses the shared factory so images, PDFs (iframe), video and audio
  // attachments all open exactly like the Stimulus-driven galleries (drive,
  // task file groups, legacy chat) — download button, caption, srcset, etc.
  useEffect(() => {
    if (!listRef.current) return
    const lb = createPhotoSwipeLightbox(listRef.current)
    return () => lb.destroy()
  }, []) // eslint-disable-line

  // Scroll listener — attached once on mount, so it must dispatch through refs
  // rather than closing over any render-time value directly:
  //   - keeps pinnedToBottomRef current so the ResizeObserver below and the
  //     image-load re-pin logic know whether to snap back to the bottom
  //   - triggers loadOlderRef.current() (the freshest loadOlder(), see the
  //     ref-sync effect further down) once the user scrolls within
  //     TOP_PRELOAD_THRESHOLD px of the top — loadOlder() itself no-ops via
  //     readyForScrollRef/loadingRef/hasMore/oldestId if a fetch isn't due
  useEffect(() => {
    const list = listRef.current
    if (!list) return
    function onScroll() {
      pinnedToBottomRef.current = isNearBottom(list)
      if (scrollPersistKey) snapshotChatScrollList(scrollPersistKey, list)
      if (scrollSideEffectsSuppressed()) return
      if (list.scrollTop >= 0 && list.scrollTop < TOP_PRELOAD_THRESHOLD) {
        loadOlderRef.current?.()
      }
    }
    list.addEventListener('scroll', onScroll, { passive: true })

    return () => list.removeEventListener('scroll', onScroll)
  }, [scrollPersistKey]) // eslint-disable-line

  // Unified "keep at bottom" mechanism. A single ResizeObserver watches the
  // message content wrapper: ANY event that changes the conversation height at
  // any level (new/edited/deleted messages, reactions, lazy images finishing,
  // async document-preview swaps, older-message prepends, avatar/receipt rows,
  // fonts) grows or shrinks the wrapper. When the user was at the bottom before
  // the change, we smoothly scroll to the very end. This replaces the previous
  // ad-hoc per-event handlers (image `load`, preview-ready custom event) with
  // one place that reasons purely about "did the content get taller while the
  // user was pinned to the bottom".
  useEffect(() => {
    const list    = listRef.current
    const content = contentRef.current
    if (!list || !content) return

    let lastHeight = content.offsetHeight

    const ro = new ResizeObserver(() => {
      const newHeight = content.offsetHeight
      const grew      = newHeight > lastHeight + 1
      lastHeight = newHeight

      // Only react to growth, and only when the user was at (or near) the
      // bottom before this layout change. Programmatic prepends (older messages)
      // clear pinnedToBottomRef via the scroll handler, so they won't trigger.
      if (!grew) return
      if (scrollSideEffectsSuppressed()) return
      if (!pinnedToBottomRef.current) return

      // Smooth scroll to the very end. rAF ensures we read the final
      // scrollHeight after the browser has committed the new layout.
      requestAnimationFrame(() => {
        list.scrollTo({ top: list.scrollHeight, behavior: 'smooth' })
      })
    })

    ro.observe(content)
    return () => ro.disconnect()
  }, []) // eslint-disable-line

  // Pin to bottom + arm sound on first message batch (handles async load after mount)
  useEffect(() => {
    if (!messages.length) return

    if (!initialPinDoneRef.current) {
      initialPinDoneRef.current = true
      lastMsgScrollAtRef.current = Date.now()
      const saved = scrollPersistKey ? readChatScrollPersist(scrollPersistKey) : null
      if (saved?.pinned) {
        pinToBottomOnOpen()
      } else if (saved && saved.scrollTop > 0) {
        restoreScrollPosition(saved.scrollTop)
      } else {
        pinToBottomOnOpen()
      }
    }

    // Arm after the first real message batch arrives (not just on mount,
    // because messages load async and the list is empty at mount time).
    if (!soundReadyRef.current) {
      requestAnimationFrame(() => {
        messages.forEach(m => {
          const id = String(m.id ?? m._clientId ?? '')
          if (id) seenIdsRef.current.add(id)
        })
        soundReadyRef.current = true
        prevLengthRef.current = messages.length
      })
    }

    // Re-resolve watermark → receipt avatars now that messages are loaded.
    // (The receipt_snapshot may arrive before messages are in the store.)
    applyWatermarksToReceipts(store)
  }, [messages.length]) // eslint-disable-line

  // Detect new messages (after initial arm). loadOlder() pre-marks prepended
  // older messages as seen (see below) so this effect only ever treats truly
  // new (live/incoming) messages as "new" — otherwise every older page load
  // would look identical to an incoming message and wrongly play the
  // notification sound / ack a "delivered" receipt for a message the user
  // already had.
  useEffect(() => {
    if (!soundReadyRef.current) return

    const list = listRef.current
    if (!list) return

    // Find new messages not yet seen
    const newMsgs = messages.filter(m => {
      const id = String(m.id ?? m._clientId ?? '')
      return id && !seenIdsRef.current.has(id)
    })

    newMsgs.forEach(m => {
      const id = String(m.id ?? m._clientId ?? '')
      if (id) seenIdsRef.current.add(id)
    })

    if (newMsgs.length === 0) return

    const latestNew = newMsgs[newMsgs.length - 1]
    const isOwn     = latestNew.creator_person?.id === personId || !!latestNew._sendStatus

    if (!isOwn) {
      playNotificationSound()
      if (latestNew.id) ackDelivered(roomId, latestNew.id)
    }

    if (isOwn || isNearBottom(list)) {
      scrollToBottomSoon(list)
    }
  }, [messages.length]) // eslint-disable-line

  // Keep the ref pointed at the latest loadOlder closure every render, so the
  // mount-once scroll listener above always calls a fresh loadOlder().
  useEffect(() => {
    loadOlderRef.current = loadOlder
  }) // no deps — runs every render intentionally

  function restoreScrollPosition(targetTop) {
    readyForScrollRef.current = false
    pinnedToBottomRef.current = false

    const tryRestore = (attempt = 0) => {
      const list = listRef.current
      if (!list) return
      const hasContent = list.scrollHeight > list.clientHeight + 4
      if (hasContent || attempt >= 20) {
        list.style.scrollBehavior = 'auto'
        list.scrollTop = Math.min(targetTop, Math.max(0, list.scrollHeight - list.clientHeight))
        requestAnimationFrame(() => {
          list.scrollTop = Math.min(targetTop, Math.max(0, list.scrollHeight - list.clientHeight))
          list.style.scrollBehavior = ''
          isRevealed.value = true
          readyForScrollRef.current = true
          pinnedToBottomRef.current = isNearBottom(list)
        })
        return
      }
      requestAnimationFrame(() => tryRestore(attempt + 1))
    }
    tryRestore()
  }

  function pinToBottomOnOpen() {
    readyForScrollRef.current = false

    const tryPin = (attempt = 0) => {
      const list = listRef.current
      if (!list) return

      // Wait until the list has actual scrollable content, not just an empty container.
      const hasContent = list.scrollHeight > list.clientHeight + 4
      if (hasContent || attempt >= 20) {
        list.style.scrollBehavior = 'auto'
        list.scrollTop = list.scrollHeight
        requestAnimationFrame(() => {
          list.scrollTop = list.scrollHeight
          requestAnimationFrame(() => {
            list.scrollTop = list.scrollHeight
            list.style.scrollBehavior = ''
            // Arm the image-load repin window and mark as pinned.
            lastMsgScrollAtRef.current = Date.now()
            pinnedToBottomRef.current  = true
            readyForScrollRef.current = true
            // Eagerly check for an older page in case the whole conversation
            // is shorter than TOP_PRELOAD_THRESHOLD — scrollTop would then
            // already sit within the trigger zone with no scroll event to
            // fire onScroll and kick off the fetch.
            loadOlderRef.current?.()
            // Reveal the message list now that it is scrolled to the bottom.
            // This prevents the "flash of messages appearing at the top then
            // jumping down" effect when opening a room for the first time.
            isRevealed.value = true
          })
        })
      } else {
        requestAnimationFrame(() => tryPin(attempt + 1))
      }
    }

    requestAnimationFrame(() => tryPin())
  }

  function loadOlder() {
    if (scrollSideEffectsSuppressed()) return
    if (!readyForScrollRef.current) return
    if (loadingRef.current) return
    if (!hasMore) return
    if (!oldestId) return

    loadingRef.current = true

    const list             = listRef.current
    const prevScrollHeight = list?.scrollHeight ?? 0
    const prevScrollTop    = list?.scrollTop    ?? 0

    chatApi.messages.list(roomId, { before: oldestId }).then(async res => {
      if (!res.ok) {
        loadingRef.current = false
        return
      }
      const data       = await res.json
      const older      = data.messages ?? data ?? []
      const newHasMore = data.has_more ?? older.length >= PAGE_SIZE
      const newOldest  = older[0]?.id ?? null

      // Mark the prepended page as already-seen BEFORE the store update
      // triggers a re-render — otherwise the "detect new messages" effect
      // above sees these older messages as unseen and mistakes them for
      // live incoming messages (wrong notification sound + delivered ack).
      older.forEach(m => {
        const id = String(m.id ?? m._clientId ?? '')
        if (id) seenIdsRef.current.add(id)
      })

      const existing = store.messagesByRoom.value[roomId] ?? []
      store.messagesByRoom.value = {
        ...store.messagesByRoom.value,
        [roomId]: [...older, ...existing],
      }
      store.hasMoreByRoom.value = {
        ...store.hasMoreByRoom.value,
        [roomId]: newHasMore,
      }
      if (newOldest) {
        store.oldestIdByRoom.value = {
          ...store.oldestIdByRoom.value,
          [roomId]: newOldest,
        }
      }

      // Restore scroll anchor after prepend
      requestAnimationFrame(() => {
        if (list) {
          list.style.scrollBehavior = 'auto'
          list.scrollTop = prevScrollTop + (list.scrollHeight - prevScrollHeight)
          requestAnimationFrame(() => {
            list.scrollTop = prevScrollTop + (list.scrollHeight - prevScrollHeight)
            list.style.scrollBehavior = ''
          })
        }
        loadingRef.current = false
        // Still within the preload zone (e.g. the restored scroll position is
        // still close to the top, or the whole room fits in one extra page) —
        // chain straight into the next older page without waiting for another
        // scroll event.
        if (newHasMore && list && list.scrollTop < TOP_PRELOAD_THRESHOLD) {
          loadOlderRef.current?.()
        }
      })
    }).catch(() => {
      loadingRef.current = false
    })
  }

  function isNearBottom(list) {
    if (!list) return true
    if (list.scrollHeight <= list.clientHeight) return true
    return list.scrollTop + list.clientHeight >= list.scrollHeight - NEAR_BOTTOM_THRESHOLD
  }

  function scrollToBottomSoon(list) {
    if (!list) return
    lastMsgScrollAtRef.current  = Date.now()
    pinnedToBottomRef.current   = true
    list.style.scrollBehavior = 'auto'
    list.scrollTop = list.scrollHeight
    requestAnimationFrame(() => {
      list.scrollTop = list.scrollHeight
      requestAnimationFrame(() => {
        list.scrollTop = list.scrollHeight
        list.style.scrollBehavior = ''
      })
    })
  }

  function ackDelivered(rId, msgId) {
    if (!rId || !msgId) return
    chatApi.messages.delivered(rId, msgId).catch(() => { /* non-fatal */ })
  }

  const searchMatches  = store.searchMatches.value
  const searchIdx      = store.searchIdx.value
  const highlightMsgId = store.highlightMsgId.value

  return (
    <div
      class="chat-messages-list"
      ref={listRef}
      id={roomId ? `chat-room-${roomId}-messages` : undefined}
    >
      {/* Full-area skeleton loader while the first message page is loading */}
      {isLoadingMsgs && messages.length === 0 && (
        <div class="chat-messages-loader">
          <span class="spinner-border spinner-border-sm text-secondary" role="status" />
        </div>
      )}

      {/* Top marker — purely informational (data-has-more) plus the loading
          spinner while an older page is in flight. Pagination itself is
          driven by the scroll listener's TOP_PRELOAD_THRESHOLD check above,
          not by observing this element. */}
      <div
        class="chat-scroll-sentinel"
        data-has-more={hasMore ? 'true' : 'false'}
      >
        {loadingRef.current && (
          <div class="chat-sentinel-spinner">
            <span class="spinner-border spinner-border-sm text-secondary" role="status" />
          </div>
        )}
      </div>

      {messages.length === 0 && !hasMore && !isLoadingMsgs && (
        <div class="chat-messages-empty">
          <i class="bi bi-chat-dots" />
          <span>{t('chat.messages.no_messages')}</span>
        </div>
      )}

      {/* Content wrapper — observed by the ResizeObserver so any height change
          (new messages, images, preview swaps, reactions, edits) keeps the view
          pinned to the bottom when the user was already there.
          The `is-revealed` class fades the content in once the list is scrolled
          to the bottom on room open, preventing a flash of messages at the top. */}
      <div ref={contentRef} class={`chat-messages-content${isRevealed.value ? ' is-revealed' : ''}`}>
        {messages.map((message, idx) => {
          const prevMessage = idx > 0 ? messages[idx - 1] : null
          const msgId       = message.id ?? message._clientId
          const highlighted = (
            highlightMsgId === msgId ||
            (searchMatches.length > 0 && searchMatches[searchIdx] === msgId)
          )
          return (
            <MessageBubble
              key={msgId}
              message={message}
              prevMessage={prevMessage}
              store={store}
              cable={cable}
              highlighted={highlighted}
              allMessages={messages}
            />
          )
        })}
      </div>
    </div>
  )
}
